import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_DAILY_METRICS, GET_MONTHLY_METRICS } from '../graphql/admin';
import { AdminLayout } from '../components/AdminLayout';

export function AdminAnalyticsPage() {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [metricType, setMetricType] = useState<'users' | 'revenue' | 'engagement'>('users');

  const daysBack = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;

  const { data: dailyData, loading: dailyLoading } = useQuery(GET_DAILY_METRICS, {
    variables: { daysBack },
  });

  const { data: monthlyData, loading: monthlyLoading } = useQuery(GET_MONTHLY_METRICS, {
    variables: { monthsBack: 12 },
  });

  const dailyMetrics = dailyData?.adminDailyMetrics || [];
  const monthlyMetrics = monthlyData?.adminMonthlyMetrics || [];

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatMonth = (year: number, month: number) => {
    return new Date(year, month - 1).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
    });
  };

  // Calculate simple statistics
  const calculateGrowth = (metrics: any[], key: string) => {
    if (metrics.length < 2) return 0;
    const latest = metrics[metrics.length - 1]?.[key] || 0;
    const previous = metrics[metrics.length - 2]?.[key] || 0;
    if (previous === 0) return 0;
    return ((latest - previous) / previous) * 100;
  };

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Analytics & Insights</h1>
          <p className="text-gray-600 mt-1">Track growth, revenue, and engagement over time</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex gap-4 items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time Range</label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Metric Type</label>
              <select
                value={metricType}
                onChange={(e) => setMetricType(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="users">User Metrics</option>
                <option value="revenue">Revenue Metrics</option>
                <option value="engagement">Engagement Metrics</option>
              </select>
            </div>
          </div>
        </div>

        {/* Daily Metrics Chart */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Daily Trends</h2>
          {dailyLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-pulse">Loading chart data...</div>
            </div>
          ) : (
            <>
              {metricType === 'users' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded">
                      <div className="text-2xl font-bold text-blue-600">
                        {formatNumber(dailyMetrics[dailyMetrics.length - 1]?.totalUsers || 0)}
                      </div>
                      <div className="text-sm text-gray-600">Total Users</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {calculateGrowth(dailyMetrics, 'totalUsers').toFixed(1)}% vs yesterday
                      </div>
                    </div>
                  <div className="text-center p-4 bg-green-50 rounded">
                    <div className="text-2xl font-bold text-green-600">
                      {formatNumber(
                        dailyMetrics.reduce(
                          (sum: number, m: any) => sum + (m.newUsersToday || 0),
                          0,
                        ),
                      )}
                    </div>
                    <div className="text-sm text-gray-600">New Users ({timeRange})</div>
                  </div>
                    <div className="text-center p-4 bg-purple-50 rounded">
                      <div className="text-2xl font-bold text-purple-600">
                        {formatNumber(dailyMetrics[dailyMetrics.length - 1]?.activeUsersToday || 0)}
                      </div>
                      <div className="text-sm text-gray-600">Active Today (DAU)</div>
                    </div>
                  </div>

                  {/* Simple bar chart using divs */}
                  <div className="mt-6">
                    <div className="text-sm font-medium text-gray-700 mb-2">New Users per Day</div>
                    <div className="flex items-end justify-between gap-1 h-48">
                      {dailyMetrics
                        .slice(-(timeRange === '7d' ? 7 : 30))
                        .map((metric: any, i: number) => {
                        const maxValue = Math.max(
                          ...dailyMetrics.map((m: any) => m.newUsersToday || 0),
                        );
                        const height = maxValue > 0 ? ((metric.newUsersToday || 0) / maxValue) * 100 : 0;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center">
                            <div
                              className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-all cursor-pointer relative group"
                              style={{ height: `${height}%`, minHeight: height > 0 ? '4px' : '0' }}
                            >
                              <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                {metric.newUsersToday || 0} users
                                <br />
                                {formatDate(metric.date)}
                              </div>
                            </div>
                            {i % Math.floor(dailyMetrics.length / 5) === 0 && (
                              <div className="text-xs text-gray-500 mt-1">
                                {formatDate(metric.date)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {metricType === 'revenue' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-green-50 rounded">
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(dailyMetrics[dailyMetrics.length - 1]?.mrr || 0)}
                      </div>
                      <div className="text-sm text-gray-600">Current MRR</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {calculateGrowth(dailyMetrics, 'mrr').toFixed(1)}% vs yesterday
                      </div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded">
                      <div className="text-2xl font-bold text-blue-600">
                        {formatCurrency(
                          dailyMetrics.reduce(
                            (sum: number, m: any) => sum + (m.newMrrToday || 0),
                            0,
                          ),
                        )}
                      </div>
                      <div className="text-sm text-gray-600">New MRR ({timeRange})</div>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded">
                      <div className="text-2xl font-bold text-red-600">
                        {formatCurrency(
                          dailyMetrics.reduce(
                            (sum: number, m: any) => sum + (m.churnedMrrToday || 0),
                            0,
                          ),
                        )}
                      </div>
                      <div className="text-sm text-gray-600">Churned MRR ({timeRange})</div>
                    </div>
                  </div>

                  {/* MRR Growth Chart */}
                  <div className="mt-6">
                    <div className="text-sm font-medium text-gray-700 mb-2">MRR Growth</div>
                    <div className="flex items-end justify-between gap-1 h-48">
                      {dailyMetrics.slice(-30).map((metric: any, i: number) => {
                        const maxValue = Math.max(...dailyMetrics.map((m: any) => m.mrr || 0));
                        const height = maxValue > 0 ? ((metric.mrr || 0) / maxValue) * 100 : 0;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center">
                            <div
                              className="w-full bg-green-500 rounded-t hover:bg-green-600 transition-all cursor-pointer relative group"
                              style={{ height: `${height}%`, minHeight: height > 0 ? '4px' : '0' }}
                            >
                              <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                {formatCurrency(metric.mrr || 0)}
                                <br />
                                {formatDate(metric.date)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {metricType === 'engagement' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-purple-50 rounded">
                      <div className="text-2xl font-bold text-purple-600">
                        {(dailyMetrics[dailyMetrics.length - 1]?.avgContextsPerOrg || 0).toFixed(1)}
                      </div>
                      <div className="text-sm text-gray-600">Avg Contexts per Org</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded">
                      <div className="text-2xl font-bold text-blue-600">
                        {(dailyMetrics[dailyMetrics.length - 1]?.avgProjectsPerOrg || 0).toFixed(1)}
                      </div>
                      <div className="text-sm text-gray-600">Avg Projects per Org</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded">
                      <div className="text-2xl font-bold text-orange-600">
                        {formatNumber(dailyMetrics[dailyMetrics.length - 1]?.totalContexts || 0)}
                      </div>
                      <div className="text-sm text-gray-600">Total Contexts</div>
                    </div>
                  </div>

                  {/* Contexts created chart */}
                  <div className="mt-6">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Contexts Created per Day
                    </div>
                    <div className="flex items-end justify-between gap-1 h-48">
                      {dailyMetrics.slice(-30).map((metric: any, i: number) => {
                        const maxValue = Math.max(
                          ...dailyMetrics.map((m: any) => m.contextsCreatedToday || 0),
                        );
                        const height =
                          maxValue > 0 ? ((metric.contextsCreatedToday || 0) / maxValue) * 100 : 0;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center">
                            <div
                              className="w-full bg-purple-500 rounded-t hover:bg-purple-600 transition-all cursor-pointer relative group"
                              style={{ height: `${height}%`, minHeight: height > 0 ? '4px' : '0' }}
                            >
                              <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                {metric.contextsCreatedToday || 0} contexts
                                <br />
                                {formatDate(metric.date)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Monthly Summary */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Monthly Summary (Last 12 Months)</h2>
          {monthlyLoading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="animate-pulse">Loading monthly data...</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Month
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">MAU</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">MRR</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">ARR</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      New Users
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      User Churn
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Revenue Growth
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyMetrics.map((metric: any) => (
                    <tr key={`${metric.year}-${metric.month}`} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">
                        {formatMonth(metric.year, metric.month)}
                      </td>
                      <td className="py-3 px-4">{formatNumber(metric.mau)}</td>
                      <td className="py-3 px-4">{formatCurrency(metric.mrr)}</td>
                      <td className="py-3 px-4">{formatCurrency(metric.arr)}</td>
                      <td className="py-3 px-4 text-green-600">+{formatNumber(metric.newUsers)}</td>
                      <td className="py-3 px-4">
                        <span
                          className={metric.userChurnRate > 5 ? 'text-red-600' : 'text-gray-900'}
                        >
                          {metric.userChurnRate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={
                            metric.revenueGrowthRate >= 0 ? 'text-green-600' : 'text-red-600'
                          }
                        >
                          {metric.revenueGrowthRate >= 0 ? '+' : ''}
                          {metric.revenueGrowthRate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Note about charts */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Currently using basic CSS charts. For production, consider
            installing recharts or chart.js for more sophisticated visualizations with tooltips,
            legends, and interactive features.
          </p>
        </div>
      </div>
    </AdminLayout>
  );
}
